import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {BaseTask, User} from '@ee/common/models';
import {TaskType} from '@ee/common/enums';

/**
 * Eviction step-deadline editor: a pencil/"Set Step Due Date" trigger that opens the shared
 * `ee-step-scheduler-panel` (calendar + searchable assignees) in a CDK overlay. The panel owns the
 * date/assignee UI; this component owns the trigger + overlay and maps the panel's `{dueDate, userIds}`
 * result onto the eviction `BaseTask` shape it emits via `onSave`.
 */
@Component({
  selector: 'ee-workflow-task-info',
  template: `
    <div #trigger="cdkOverlayOrigin" cdkOverlayOrigin>
      @if (task) {
        <button mat-icon-button type="button" class="ee__button ee__large md-gray-bg mr-4" aria-label="Change due date"
                tp="Update Deadline" (click)="detailsOpen = !detailsOpen" [class.open]="detailsOpen">
          <mat-icon fontSet="fa" fontIcon="fa-pen"></mat-icon>
        </button>
      } @else {
        <a cdkOverlayOrigin class="ee__link font-medium text-sm step-due-date-creation-link"
          (click)="detailsOpen = !detailsOpen"
          [class.open]="detailsOpen">
          Set Step Due Date
        </a>
      }
    </div>
    <ng-template
      cdkConnectedOverlay
      cdkConnectedOverlayPush
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayHasBackdrop]="false"
      [cdkConnectedOverlayOpen]="detailsOpen"
      [cdkConnectedOverlayDisableClose]="false"
      [cdkConnectedOverlayDisposeOnNavigation]="true"
      (overlayOutsideClick)="detailsOpen = false">
      @if (detailsOpen) {
        <ee-step-scheduler-panel
          [dueDate]="task?.due_date"
          [assignedUserIds]="task?.assigned_users"
          [users]="users"
          (onSave)="onPanelSave($event)"
          (onCancel)="detailsOpen = false"></ee-step-scheduler-panel>
      }
    </ng-template>
    `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class WorkflowTaskInfoComponent {
  @Input({required: false}) task: BaseTask;
  @Input({required: false}) users: User[];
  @Output() onSave = new EventEmitter<BaseTask>();
  public detailsOpen = false;

  /** Map the shared panel's result onto the eviction BaseTask shape (preserving the existing task fields). */
  onPanelSave(result: {dueDate: string | null; userIds: string[]}): void {
    this.detailsOpen = false;
    this.onSave.emit({
      ...(this.task ?? {id: null, name: '', type: TaskType.WORKFLOW_STEP}),
      due_date: result.dueDate,
      assigned_users: result.userIds
    });
  }
}
